<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useMapStore } from '@/stores/map';
import { useEditorStore } from '@/stores/editor';

const props = defineProps<{
    floating?: boolean;
}>();

const emit = defineEmits<{
    (e: 'toggle-float'): void;
}>();

const mapStore = useMapStore();
const editorStore = useEditorStore();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const minimapSize = 200;

// Compute scale factor
const scale = computed(() => {
    if (!mapStore.hasMap) return 1;
    const maxDim = Math.max(mapStore.mapWidth, mapStore.mapHeight);
    return minimapSize / maxDim;
});

// Draw minimap
function drawMinimap(): void {
    if (!canvasRef.value || !mapStore.document) return;

    const canvas = canvasRef.value;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height, tiles } = mapStore.document;
    const s = scale.value;

    // Clear
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, minimapSize, minimapSize);

    // Draw tiles
    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const tile = tiles[x][y];

            // Base tile color (simplified)
            let color = '#2d5a27'; // Default grass green

            // Different colors based on tile sprite
            if (tile.tileSprite >= 300 && tile.tileSprite < 320) {
                color = '#505050'; // Stone
            } else if (tile.tileSprite >= 320 && tile.tileSprite < 330) {
                color = '#e8e8e8'; // Snow
            } else if (tile.tileSprite >= 330 && tile.tileSprite < 349) {
                color = '#c4a35a'; // Sand
            } else if (tile.tileSprite === 19 || tile.tileSprite === 366 || tile.tileSprite === 367) {
                color = '#3498db'; // Water
            } else if (tile.tileSprite >= 50 && tile.tileSprite < 100) {
                color = '#8b4513'; // Structures
            } else if (tile.tileSprite >= 70 && tile.tileSprite < 100) {
                color = '#696969'; // Interior
            }

            // Object overlay
            if (tile.objectSprite >= 100 && tile.objectSprite < 200) {
                color = '#228b22'; // Trees
            } else if (tile.objectSprite >= 200) {
                color = '#8b4513'; // Objects
            }

            // Blocked overlay
            if (tile.blocked) {
                color = '#ff4444';
            }

            // Teleport overlay
            if (tile.teleport) {
                color = '#9b59b6';
            }

            ctx.fillStyle = color;
            ctx.fillRect(
                Math.floor(x * s),
                Math.floor(y * s),
                Math.ceil(s) || 1,
                Math.ceil(s) || 1
            );
        }
    }

    // Cursor/viewport is drawn on separate canvas layer
}

// Click on minimap to navigate
function handleClick(event: MouseEvent): void {
    if (!canvasRef.value || !mapStore.hasMap) return;

    const rect = canvasRef.value.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const tileX = Math.floor(x / scale.value);
    const tileY = Math.floor(y / scale.value);

    // Calculate pan to center on clicked tile
    const viewportWidth = 800;
    const viewportHeight = 600;

    editorStore.panX = viewportWidth / 2 - tileX * 32 * editorStore.zoom;
    editorStore.panY = viewportHeight / 2 - tileY * 32 * editorStore.zoom;
}

// Separate canvas for cursor overlay (avoids redrawing entire map)
const cursorCanvasRef = ref<HTMLCanvasElement | null>(null);

// Draw just the cursor overlay (cheap - called on mouse move)
function drawCursor(): void {
    if (!cursorCanvasRef.value || !mapStore.document) return;

    const canvas = cursorCanvasRef.value;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const s = scale.value;

    // Clear cursor layer
    ctx.clearRect(0, 0, minimapSize, minimapSize);

    // Draw viewport rectangle
    if (editorStore.zoom > 0) {
        const viewWidth = 800 / (32 * editorStore.zoom);
        const viewHeight = 600 / (32 * editorStore.zoom);
        const viewX = -editorStore.panX / (32 * editorStore.zoom);
        const viewY = -editorStore.panY / (32 * editorStore.zoom);

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(viewX * s, viewY * s, viewWidth * s, viewHeight * s);
    }

    // Draw cursor position
    if (editorStore.tileX >= 0 && editorStore.tileY >= 0) {
        ctx.fillStyle = '#ffff00';
        ctx.fillRect(
            Math.floor(editorStore.tileX * s),
            Math.floor(editorStore.tileY * s),
            Math.max(2, Math.ceil(s)),
            Math.max(2, Math.ceil(s))
        );
    }
}

// Watch for map changes - only redraw tiles when map actually changes
let lastMapKey: string | null = null;
watch(() => mapStore.document, (doc) => {
    if (doc) {
        const mapKey = `${doc.filename}:${doc.width}x${doc.height}`;
        if (mapKey !== lastMapKey) {
            lastMapKey = mapKey;
            drawMinimap();
        }
    }
});

// Throttle cursor updates using requestAnimationFrame
let cursorUpdateScheduled = false;
function scheduleCursorUpdate(): void {
    if (cursorUpdateScheduled) return;
    cursorUpdateScheduled = true;
    requestAnimationFrame(() => {
        cursorUpdateScheduled = false;
        drawCursor();
    });
}

// Watch for cursor/viewport changes - throttled updates
watch(() => editorStore.tileX, scheduleCursorUpdate);
watch(() => editorStore.tileY, scheduleCursorUpdate);
watch(() => editorStore.zoom, scheduleCursorUpdate);
watch(() => editorStore.panX, scheduleCursorUpdate);
watch(() => editorStore.panY, scheduleCursorUpdate);

onMounted(() => {
    drawMinimap();
    drawCursor();
});
</script>

<template>
    <div class="minimap">
        <div class="panel-header">
            <h3>Minimap</h3>
            <button
                class="float-toggle-btn"
                @click="emit('toggle-float')"
                :title="props.floating ? 'Dock to sidebar' : 'Pop out'"
            >
                {{ props.floating ? '⇲' : '⇱' }}
            </button>
        </div>
        <div class="minimap-container">
            <div class="minimap-canvas-wrapper">
                <canvas
                    ref="canvasRef"
                    class="minimap-base"
                    :width="minimapSize"
                    :height="minimapSize"
                    @click="handleClick"
                />
                <canvas
                    ref="cursorCanvasRef"
                    class="minimap-cursor"
                    :width="minimapSize"
                    :height="minimapSize"
                />
            </div>
        </div>
        <div class="minimap-info" v-if="mapStore.hasMap">
            <span>{{ mapStore.mapWidth }} x {{ mapStore.mapHeight }}</span>
        </div>
    </div>
</template>

<style scoped>
.minimap {
    overflow: hidden;
    flex-shrink: 0;
    border-top: 1px solid var(--border-color);
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.panel-header h3 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
}

.float-toggle-btn {
    width: 22px;
    height: 22px;
    padding: 0;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
}

.float-toggle-btn:hover {
    background: var(--accent-color);
    border-color: var(--accent-color);
    color: white;
}

.minimap-container {
    padding: 8px;
    display: flex;
    justify-content: center;
}

.minimap-canvas-wrapper {
    position: relative;
    width: 200px;
    height: 200px;
}

.minimap-base,
.minimap-cursor {
    position: absolute;
    top: 0;
    left: 0;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    cursor: crosshair;
    image-rendering: pixelated;
}

.minimap-cursor {
    pointer-events: none;
}

.minimap-info {
    padding: 4px 12px 8px;
    text-align: center;
    font-size: 11px;
    color: var(--text-secondary);
}
</style>
